import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

// Unified movement type that aggregates from multiple sources
export type MovementType =
  | 'sale'
  | 'transfer_placed'
  | 'transfer_received'
  | 'transfer_in_transit'
  | 'inventory_count_gain'
  | 'inventory_count_loss'
  | 'supplier_order'
  | 'supplier_return'
  | 'adjustment'
  | 'waste'
  | 'boh_to_foh'
  | 'foh_to_boh'
  | 'consumed';

export interface InventoryMovement {
  id: string;
  date: string;
  movement_type: MovementType;
  movement_label: string;
  from_location_id: string | null;
  from_location_name: string | null;
  from_stock_before: number | null;
  from_stock_after: number | null;
  from_status: 'on_hand' | 'in_transit' | 'inventory_count' | null;
  to_location_id: string | null;
  to_location_name: string | null;
  to_stock_before: number | null;
  to_stock_after: number | null;
  to_status: 'on_hand' | 'in_transit' | 'inventory_count' | null;
  user_id: string | null;
  user_name: string | null;
  user_initials: string | null;
  quantity: number;
  product_id: string | null;
  product_name: string | null;
  raw_item_id: string | null;
  raw_item_name: string | null;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
}

export interface MovementFilters {
  movement_type?: MovementType | 'all';
  location_id?: string | 'all';
  product_id?: string;
  raw_item_id?: string;
  date_from?: string;
  date_to?: string;
}

interface UseInventoryMovementsOptions {
  filters?: MovementFilters;
  limit?: number;
}

export interface InventoryMovementsPage {
  movements: InventoryMovement[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface UseInventoryMovementsPageOptions {
  filters?: MovementFilters;
  itemType?: 'all' | 'sellable' | 'raw_goods';
  search?: string;
  page?: number;
  pageSize?: number;
}

// Helper to format movement type labels
export function getMovementLabel(type: MovementType): string {
  const labels: Record<MovementType, string> = {
    sale: 'Sale',
    transfer_placed: 'Transfer placed',
    transfer_received: 'Transfer received',
    transfer_in_transit: 'Transfer in transit',
    inventory_count_gain: 'Inventory count (gain)',
    inventory_count_loss: 'Inventory count (loss)',
    supplier_order: 'Supplier order',
    supplier_return: 'Supplier return',
    adjustment: 'Adjustment',
    waste: 'Waste/Loss',
    boh_to_foh: 'BOH to FOH',
    foh_to_boh: 'FOH to BOH',
    consumed: 'Consumed (composite)',
  };
  return labels[type] || type;
}

// Helper to get movement type color classes
export function getMovementColor(type: MovementType): string {
  const colors: Record<MovementType, string> = {
    sale: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    transfer_placed: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    transfer_received: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    transfer_in_transit: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    inventory_count_gain: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    inventory_count_loss: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    supplier_order: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    supplier_return: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
    adjustment: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    waste: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    boh_to_foh: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
    foh_to_boh: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
    consumed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
}

// Helper to get user initials
function getInitials(firstName: string | null, lastName: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

export function useInventoryMovements(options: UseInventoryMovementsOptions = {}) {
  const { organization } = useAuth();
  const { filters = {}, limit = 100 } = options;

  return useQuery({
    queryKey: ['inventory-movements', organization?.id, filters, limit],
    queryFn: async (): Promise<InventoryMovement[]> => {
      if (!organization?.id) return [];

      const movements: InventoryMovement[] = [];

      // 1. Fetch from inventory_transactions (sales, adjustments, waste, etc.)
      const { data: transactions, error: txnError } = await supabase
        .from('inventory_transactions')
        .select(`
          id,
          created_at,
          transaction_type,
          quantity_change,
          quantity_before,
          quantity_after,
          reference_type,
          reference_id,
          notes,
          user:profiles(id, first_name, last_name),
          inventory_item:inventory_items(
            id,
            location_id,
            product:products(id, name),
            location:locations(id, name)
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (txnError) {
        console.error('Error fetching transactions:', txnError);
      } else if (transactions) {
        for (const txn of transactions) {
          // Inter-location and BOH/FOH transfers have canonical movement rows
          // below. Do not render their underlying debit/credit audit rows again.
          if (
            txn.reference_type === 'transfer' ||
            txn.transaction_type === 'transfer_boh_to_foh' ||
            txn.transaction_type === 'transfer_foh_to_boh'
          ) {
            continue;
          }

          const invItem = txn.inventory_item as any;
          const user = txn.user as any;
          const location = invItem?.location;
          const product = invItem?.product;

          // Determine movement type from transaction_type
          let movementType: MovementType = 'adjustment';
          if (txn.transaction_type === 'sold') movementType = 'sale';
          else if (txn.transaction_type === 'received') movementType = 'supplier_order';
          else if (txn.transaction_type === 'returned') movementType = 'supplier_return';
          else if (txn.transaction_type === 'waste') movementType = 'waste';
          else if (txn.transaction_type === 'consumed') movementType = 'consumed';
          else if (txn.transaction_type === 'adjusted') {
            movementType = txn.quantity_change >= 0 ? 'inventory_count_gain' : 'inventory_count_loss';
          }
          else if (txn.transaction_type === 'transfer_boh_to_foh') movementType = 'boh_to_foh';
          else if (txn.transaction_type === 'transfer_foh_to_boh') movementType = 'foh_to_boh';

          // Determine From and To based on movement type for full transparency
          let fromLocationName: string | null = null;
          let toLocationName: string | null = null;
          let fromStatus: 'on_hand' | 'in_transit' | 'inventory_count' | null = 'on_hand';
          let toStatus: 'on_hand' | 'in_transit' | 'inventory_count' | null = 'on_hand';

          switch (movementType) {
            case 'sale':
              fromLocationName = `${location?.name || 'Unknown'} (FOH)`;
              toLocationName = 'Customer';
              toStatus = null;
              break;
            case 'consumed':
              fromLocationName = `${location?.name || 'Unknown'} (FOH)`;
              toLocationName = 'Composite produced';
              toStatus = null;
              break;
            case 'supplier_order':
              fromLocationName = 'Supplier';
              toLocationName = `${location?.name || 'Unknown'} (BOH)`;
              break;
            case 'supplier_return':
              fromLocationName = `${location?.name || 'Unknown'}`;
              toLocationName = 'Supplier (Return)';
              toStatus = null;
              break;
            case 'inventory_count_gain':
            case 'inventory_count_loss':
              fromLocationName = `${location?.name || 'Unknown'} (Count)`;
              toLocationName = `${location?.name || 'Unknown'} (Adjusted)`;
              fromStatus = 'inventory_count';
              toStatus = 'on_hand';
              break;
            case 'adjustment':
              fromLocationName = txn.quantity_change < 0 ? `${location?.name || 'Unknown'}` : 'Adjustment';
              toLocationName = txn.quantity_change >= 0 ? `${location?.name || 'Unknown'}` : 'Adjustment';
              break;
            case 'waste':
              fromLocationName = `${location?.name || 'Unknown'}`;
              toLocationName = 'Waste/Loss';
              toStatus = null;
              break;
            case 'boh_to_foh':
              fromLocationName = `${location?.name || 'Unknown'} (BOH)`;
              toLocationName = `${location?.name || 'Unknown'} (FOH)`;
              break;
            case 'foh_to_boh':
              fromLocationName = `${location?.name || 'Unknown'} (FOH)`;
              toLocationName = `${location?.name || 'Unknown'} (BOH)`;
              break;
            default:
              fromLocationName = txn.quantity_change < 0 ? location?.name : null;
              toLocationName = txn.quantity_change >= 0 ? location?.name : null;
          }

          movements.push({
            id: `txn-${txn.id}`,
            date: txn.created_at,
            movement_type: movementType,
            movement_label: getMovementLabel(movementType),
            from_location_id: location?.id || null,
            from_location_name: fromLocationName,
            from_stock_before: txn.quantity_before,
            from_stock_after: txn.quantity_change < 0 ? txn.quantity_after : null,
            from_status: fromStatus,
            to_location_id: location?.id || null,
            to_location_name: toLocationName,
            to_stock_before: txn.quantity_change >= 0 ? txn.quantity_before : null,
            to_stock_after: txn.quantity_change >= 0 ? txn.quantity_after : null,
            to_status: toStatus,
            user_id: user?.id || null,
            user_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : null,
            user_initials: user ? getInitials(user.first_name, user.last_name) : null,
            quantity: Math.abs(txn.quantity_change),
            product_id: product?.id || null,
            product_name: product?.name || null,
            raw_item_id: null,
            raw_item_name: null,
            reference_type: txn.reference_type,
            reference_id: txn.reference_id,
            notes: txn.notes,
          });
        }
      }

      // 2. Fetch from raw_inventory_transactions (raw material movements)
      const { data: rawTransactions, error: rawTxnError } = await supabase
        .from('raw_inventory_transactions')
        .select(`
          id,
          created_at,
          transaction_type,
          quantity_change,
          quantity_before,
          quantity_after,
          reference_type,
          reference_id,
          notes,
          performed_by,
          user:profiles!raw_inventory_transactions_performed_by_fkey(id, first_name, last_name),
          raw_item:raw_inventory_items!raw_inventory_transactions_inventory_item_id_fkey(id, name),
          location:locations!raw_inventory_transactions_location_id_fkey(id, name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (rawTxnError) {
        console.error('Error fetching raw inventory transactions:', rawTxnError);
      } else if (rawTransactions) {
        for (const txn of rawTransactions) {
          // The goods-transfer section below is the canonical representation of
          // raw-material shipment and receipt events.
          if (txn.reference_type === 'transfer') continue;

          const rawItem = txn.raw_item as any;
          const user = txn.user as any;
          const location = txn.location as any;

          // Determine movement type from transaction_type
          let movementType: MovementType = 'adjustment';
          if (txn.transaction_type === 'received') movementType = 'supplier_order';
          else if (txn.transaction_type === 'waste') movementType = 'waste';
          else if (txn.transaction_type === 'consumed') movementType = 'consumed';
          else if (txn.transaction_type === 'count') {
            movementType = txn.quantity_change >= 0 ? 'inventory_count_gain' : 'inventory_count_loss';
          }
          else if (txn.transaction_type === 'adjustment') {
            movementType = txn.quantity_change >= 0 ? 'inventory_count_gain' : 'inventory_count_loss';
          }
          else if (txn.transaction_type === 'transfer_out') movementType = 'transfer_placed';
          else if (txn.transaction_type === 'transfer_in') movementType = 'transfer_received';

          // Determine From and To based on movement type
          let fromLocationName: string | null = null;
          let toLocationName: string | null = null;
          let fromStatus: 'on_hand' | 'in_transit' | 'inventory_count' | null = 'on_hand';
          let toStatus: 'on_hand' | 'in_transit' | 'inventory_count' | null = 'on_hand';

          switch (movementType) {
            case 'consumed':
              fromLocationName = `${location?.name || 'Unknown'}`;
              toLocationName = 'Composite produced';
              toStatus = null;
              break;
            case 'supplier_order':
              fromLocationName = 'Supplier';
              toLocationName = `${location?.name || 'Unknown'}`;
              break;
            case 'inventory_count_gain':
            case 'inventory_count_loss':
              fromLocationName = `${location?.name || 'Unknown'} (Count)`;
              toLocationName = `${location?.name || 'Unknown'} (Adjusted)`;
              fromStatus = 'inventory_count';
              toStatus = 'on_hand';
              break;
            case 'waste':
              fromLocationName = `${location?.name || 'Unknown'}`;
              toLocationName = 'Waste/Loss';
              toStatus = null;
              break;
            case 'transfer_placed':
              fromLocationName = `${location?.name || 'Unknown'}`;
              toLocationName = 'Transfer out';
              toStatus = 'in_transit';
              break;
            case 'transfer_received':
              fromLocationName = 'Transfer in';
              toLocationName = `${location?.name || 'Unknown'}`;
              fromStatus = 'in_transit';
              break;
            default:
              fromLocationName = txn.quantity_change < 0 ? `${location?.name || 'Unknown'}` : 'Adjustment';
              toLocationName = txn.quantity_change >= 0 ? `${location?.name || 'Unknown'}` : 'Adjustment';
          }

          movements.push({
            id: `raw-txn-${txn.id}`,
            date: txn.created_at,
            movement_type: movementType,
            movement_label: getMovementLabel(movementType),
            from_location_id: location?.id || null,
            from_location_name: fromLocationName,
            from_stock_before: txn.quantity_before,
            from_stock_after: txn.quantity_change < 0 ? txn.quantity_after : null,
            from_status: fromStatus,
            to_location_id: location?.id || null,
            to_location_name: toLocationName,
            to_stock_before: txn.quantity_change >= 0 ? txn.quantity_before : null,
            to_stock_after: txn.quantity_change >= 0 ? txn.quantity_after : null,
            to_status: toStatus,
            user_id: user?.id || null,
            user_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : null,
            user_initials: user ? getInitials(user.first_name, user.last_name) : null,
            quantity: Math.abs(txn.quantity_change),
            product_id: null,
            product_name: null,
            raw_item_id: rawItem?.id || null,
            raw_item_name: rawItem?.name || null,
            reference_type: txn.reference_type,
            reference_id: txn.reference_id,
            notes: txn.notes,
          });
        }
      }

      // 3. Fetch from goods_transfers (inter-location transfers)
      const { data: transfers, error: transferError } = await supabase
        .from('goods_transfers')
        .select(`
          id,
          transfer_number,
          status,
          created_at,
          shipped_at,
          received_at,
          notes,
          shipping_notes,
          receiving_notes,
          from_location:locations!goods_transfers_from_location_id_fkey(id, name),
          to_location:locations!goods_transfers_to_location_id_fkey(id, name),
          creator:profiles!goods_transfers_created_by_fkey(id, first_name, last_name),
          shipper:profiles!goods_transfers_shipped_by_fkey(id, first_name, last_name),
          receiver:profiles!goods_transfers_received_by_fkey(id, first_name, last_name),
          items:goods_transfer_items(
            id,
            quantity_sent,
            quantity_received,
            product:products(id, name),
            raw_item:raw_inventory_items(id, name)
          )
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (transferError) {
        console.error('Error fetching transfers:', transferError);
      } else if (transfers) {
        for (const transfer of transfers) {
          const fromLoc = transfer.from_location as any;
          const toLoc = transfer.to_location as any;
          const items = transfer.items as any[] || [];
          
          // For each item in the transfer
          for (const item of items) {
            const product = item.product;
            const rawItem = item.raw_item;
            const quantity = transfer.status === 'received' || transfer.status === 'partially_received'
              ? item.quantity_received ?? 0
              : item.quantity_sent;

            // Determine movement type based on status
            let movementType: MovementType = 'transfer_placed';
            let movementDate = transfer.created_at;
            let user: any = transfer.creator;

            if (transfer.status === 'in_transit') {
              movementType = 'transfer_in_transit';
              movementDate = transfer.shipped_at || transfer.created_at;
              user = transfer.shipper || transfer.creator;
            } else if (transfer.status === 'received' || transfer.status === 'partially_received') {
              movementType = 'transfer_received';
              movementDate = transfer.received_at || transfer.created_at;
              user = transfer.receiver || transfer.creator;
            }

            movements.push({
              id: `transfer-${transfer.id}-${item.id}`,
              date: movementDate,
              movement_type: movementType,
              movement_label: getMovementLabel(movementType),
              from_location_id: fromLoc?.id || null,
              from_location_name: fromLoc?.name || null,
              from_stock_before: null,
              from_stock_after: null,
              from_status: movementType === 'transfer_received' ? 'in_transit' : 'on_hand',
              to_location_id: toLoc?.id || null,
              to_location_name: toLoc?.name || null,
              to_stock_before: null,
              to_stock_after: null,
              to_status: movementType === 'transfer_placed' ? 'in_transit' : 'on_hand',
              user_id: user?.id || null,
              user_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : null,
              user_initials: user ? getInitials(user.first_name, user.last_name) : null,
              quantity,
              product_id: product?.id || null,
              product_name: product?.name || null,
              raw_item_id: rawItem?.id || null,
              raw_item_name: rawItem?.name || null,
              reference_type: 'goods_transfer',
              reference_id: transfer.id,
              notes: transfer.notes || transfer.shipping_notes || transfer.receiving_notes,
            });
          }
        }
      }

      // 4. Fetch from inventory_transfers (BOH/FOH transfers)
      const { data: bohFohTransfers, error: bohFohError } = await supabase
        .from('inventory_transfers')
        .select(`
          id,
          created_at,
          transfer_type,
          quantity,
          notes,
          user:profiles(id, first_name, last_name),
          inventory_item:inventory_items(
            id,
            location_id,
            quantity_boh,
            quantity_foh,
            product:products(id, name),
            location:locations(id, name)
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (bohFohError) {
        console.error('Error fetching BOH/FOH transfers:', bohFohError);
      } else if (bohFohTransfers) {
        for (const transfer of bohFohTransfers) {
          const invItem = transfer.inventory_item as any;
          const user = transfer.user as any;
          const location = invItem?.location;
          const product = invItem?.product;

          const movementType: MovementType = transfer.transfer_type === 'boh_to_foh' ? 'boh_to_foh' : 'foh_to_boh';
          const isBohToFoh = transfer.transfer_type === 'boh_to_foh';

          movements.push({
            id: `boh-foh-${transfer.id}`,
            date: transfer.created_at,
            movement_type: movementType,
            movement_label: getMovementLabel(movementType),
            from_location_id: location?.id || null,
            from_location_name: isBohToFoh ? `${location?.name} (BOH)` : `${location?.name} (FOH)`,
            from_stock_before: (transfer as any).quantity_before_from ?? null,
            from_stock_after: (transfer as any).quantity_after_from ?? null,
            from_status: 'on_hand',
            to_location_id: location?.id || null,
            to_location_name: isBohToFoh ? `${location?.name} (FOH)` : `${location?.name} (BOH)`,
            to_stock_before: (transfer as any).quantity_before_to ?? null,
            to_stock_after: (transfer as any).quantity_after_to ?? null,
            to_status: 'on_hand',
            user_id: user?.id || null,
            user_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : null,
            user_initials: user ? getInitials(user.first_name, user.last_name) : null,
            quantity: transfer.quantity,
            product_id: product?.id || null,
            product_name: product?.name || null,
            raw_item_id: null,
            raw_item_name: null,
            reference_type: 'inventory_transfer',
            reference_id: transfer.id,
            notes: transfer.notes,
          });
        }
      }

      // Sort all movements by date descending
      movements.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      // Apply filters
      let filtered = movements;

      if (filters.movement_type && filters.movement_type !== 'all') {
        filtered = filtered.filter(m => m.movement_type === filters.movement_type);
      }

      if (filters.location_id && filters.location_id !== 'all') {
        filtered = filtered.filter(m => 
          m.from_location_id === filters.location_id || 
          m.to_location_id === filters.location_id
        );
      }

      if (filters.product_id) {
        filtered = filtered.filter(m => m.product_id === filters.product_id);
      }

      if (filters.raw_item_id) {
        filtered = filtered.filter(m => m.raw_item_id === filters.raw_item_id);
      }

      if (filters.date_from) {
        const fromDate = new Date(filters.date_from);
        filtered = filtered.filter(m => new Date(m.date) >= fromDate);
      }

      if (filters.date_to) {
        const toDate = new Date(filters.date_to);
        toDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter(m => new Date(m.date) <= toDate);
      }

      return filtered.slice(0, limit);
    },
    enabled: !!organization?.id,
  });
}

// Server-side unified ledger pagination. This keeps every source under one
// deterministic sort and never relies on Supabase's default REST row cap.
export function useInventoryMovementsPage(options: UseInventoryMovementsPageOptions = {}) {
  const { organization } = useAuth();
  const { filters = {}, itemType = 'all', search = '', page = 1, pageSize = 500 } = options;
  const boundedPageSize = Math.min(500, Math.max(1, pageSize));
  const boundedPage = Math.max(1, page);

  return useQuery({
    queryKey: ['inventory-movements-page', organization?.id, filters, itemType, search, boundedPage, boundedPageSize],
    queryFn: async (): Promise<InventoryMovementsPage> => {
      if (!organization?.id) return { movements: [], totalCount: 0, page: boundedPage, pageSize: boundedPageSize };
      const { data, error } = await supabase.rpc('get_inventory_movements_page' as never, {
        p_organization_id: organization.id,
        p_location_id: filters.location_id && filters.location_id !== 'all' ? filters.location_id : null,
        p_movement_type: filters.movement_type && filters.movement_type !== 'all' ? filters.movement_type : null,
        p_product_id: filters.product_id || null,
        p_raw_item_id: filters.raw_item_id || null,
        p_item_type: itemType === 'all' ? null : itemType,
        p_search: search.trim() || null,
        p_date_from: filters.date_from || null,
        p_date_to: filters.date_to || null,
        p_offset: (boundedPage - 1) * boundedPageSize,
        p_limit: boundedPageSize,
      } as never);
      if (error) throw error;
      const rows = (data || []) as Array<Record<string, unknown>>;
      return {
        movements: rows.map((row) => ({
          ...row,
          quantity: Number(row.quantity || 0),
          from_stock_before: row.from_stock_before === null ? null : Number(row.from_stock_before),
          from_stock_after: row.from_stock_after === null ? null : Number(row.from_stock_after),
          to_stock_before: row.to_stock_before === null ? null : Number(row.to_stock_before),
          to_stock_after: row.to_stock_after === null ? null : Number(row.to_stock_after),
        })) as InventoryMovement[],
        totalCount: Number(rows[0]?.total_count || 0),
        page: boundedPage,
        pageSize: boundedPageSize,
      };
    },
    enabled: !!organization?.id,
    placeholderData: (previous) => previous,
  });
}

// Hook to fetch movements for a specific product
export function useProductMovements(productId: string | null, options: Omit<UseInventoryMovementsOptions, 'filters'> & { filters?: Omit<MovementFilters, 'product_id'> } = {}) {
  return useInventoryMovements({
    ...options,
    filters: {
      ...options.filters,
      product_id: productId || undefined,
    },
  });
}

// Hook to fetch movements for a specific raw item
export function useRawItemMovements(rawItemId: string | null, options: Omit<UseInventoryMovementsOptions, 'filters'> & { filters?: Omit<MovementFilters, 'raw_item_id'> } = {}) {
  return useInventoryMovements({
    ...options,
    filters: {
      ...options.filters,
      raw_item_id: rawItemId || undefined,
    },
  });
}
